import { supabase } from "@/integrations/supabase/client";
import type { Vault, VaultLedgerEntry, VaultType, WalletAvailable } from "./types";

/** List the current user's vaults (RLS-scoped). */
export async function listMyVaults(): Promise<Vault[]> {
  const { data, error } = await supabase
    .from("vaults")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Vault[];
}

/** Read the caller's available-balance snapshot (balance − locked − offline hold). */
export async function getWalletAvailable(): Promise<WalletAvailable | null> {
  const { data, error } = await supabase
    .from("wallet_available")
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as WalletAvailable | null;
}

export async function createVault(input: {
  name: string;
  type: VaultType;
  goalCents?: number;
  autoPercentBps?: number;
  unlockDelaySeconds?: number;
}): Promise<Vault> {
  const { data, error } = await supabase.rpc("vault_create", {
    p_name: input.name,
    p_type: input.type,
    p_goal_cents: input.goalCents ?? 0,
    p_auto_percent_bps: input.autoPercentBps ?? 0,
    p_unlock_delay_seconds: input.unlockDelaySeconds ?? 0,
  });
  if (error) throw error;
  return data as unknown as Vault;
}

export async function lockToVault(vaultId: string, amountCents: number, reason = "manual"): Promise<VaultLedgerEntry> {
  const { data, error } = await supabase.rpc("vault_lock", {
    p_vault_id: vaultId,
    p_amount_cents: amountCents,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as VaultLedgerEntry;
}

export async function unlockFromVault(vaultId: string, amountCents: number, reason = "manual"): Promise<VaultLedgerEntry> {
  const { data, error } = await supabase.rpc("vault_unlock", {
    p_vault_id: vaultId,
    p_amount_cents: amountCents,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as VaultLedgerEntry;
}

export async function listVaultLedger(vaultId: string, limit = 50): Promise<VaultLedgerEntry[]> {
  const { data, error } = await supabase
    .from("vault_ledger")
    .select("*")
    .eq("vault_id", vaultId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as VaultLedgerEntry[];
}

/** All lock/unlock events for the caller across every vault (RLS-scoped, newest first). */
export async function listMyVaultHistory(limit = 50): Promise<VaultLedgerEntry[]> {
  const { data, error } = await supabase
    .from("vault_ledger")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as VaultLedgerEntry[];
}
